import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import InventoryIcon from "@mui/icons-material/Inventory";
import PercentIcon from "@mui/icons-material/Percent";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../components/sidebarLayout";
import SpaCommissionManager from "../components/SpaCommissionManager";
import SpaRetailSales from "../components/SpaRetailSales";
import { getAllClienti } from "../APIs/ClientsAPI";
import { createPackage, deletePackageById, getAllPackages, updatePackage } from "../APIs/PackagesAPI";
import {
  changeSpaStoredValueStatus,
  checkSpaAppointmentAvailability,
  consumeSpaPackageCredit,
  consumeSpaStoredValue,
  createSpaPackageEntitlement,
  createSpaStoredValueInstrument,
  dateTimeWithLocalOffset,
  generateSpaPackageAppointments,
  getSpaAppointments,
  getSpaCabins,
  getSpaOperators,
  getSpaPackageEntitlements,
  getSpaStoredValueInstruments,
  getSpaTreatments,
  releaseSpaPackageCredit,
  releaseSpaStoredValue,
  reserveSpaPackageCredit,
  reserveSpaStoredValue,
  reverseSpaPackageTransaction,
  reverseSpaStoredValueTransaction,
} from "../APIs/SpaAPI";
import { usePermissions } from "../contexts/PermissionsContext";
import { useSnackbar } from "../contexts/SnackBarContext";
import { isPositiveMoney, storedValueFormErrors } from "../utils/spaStoredValue";

const emptyEntitlementForm = {
  clientId: "",
  packageId: "",
  quantity: 1,
  validFrom: "",
  expiresAt: "",
};

const emptyPackageMasterForm = {
  id: "",
  name: "",
  description: "",
  price: "",
  treatmentProductId: "",
  includedQuantity: 1,
};

const emptyStoredValueForm = {
  clientId: "",
  type: "GIFT_CARD",
  vatType: "MULTI_PURPOSE",
  initialValue: "",
  expiresAt: "",
  motivation: "",
  authorization: "",
};

const emptyPackageAction = {
  kind: "",
  item: null,
  transaction: null,
  appointmentId: "",
  quantity: 1,
  reason: "",
};

const emptyStoredValueAction = {
  kind: "",
  instrument: null,
  transaction: null,
  appointmentId: "",
  amount: "",
  status: "BLOCKED",
  reason: "",
};

const emptyAppointmentGeneration = {
  entitlement: null,
  item: null,
  startAt: "",
  customerType: "MEMBER",
  commercialStatus: "CONFIRMED",
  operatorIds: [],
  cabinIds: [],
  selectedCombination: null,
};

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

const displayPersonName = (person) => (
  [person?.name, person?.surname].filter(Boolean).join(" ").trim() || person?.email || `#${person?.id || "-"}`
);

const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const compactDateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const pad = (value) => String(value).padStart(2, "0");

const localInputToOffset = (value) => {
  if (!value) return null;
  const [date, time] = String(value).split("T");
  if (!date || !time) return null;
  return dateTimeWithLocalOffset(date, time);
};

const localDateTimeInputValue = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
);

const nextPlannerSlotInput = (intervalMinutes = 15) => {
  const date = new Date();
  date.setSeconds(0, 0);
  const nextMinute = Math.ceil(date.getMinutes() / intervalMinutes) * intervalMinutes;
  date.setMinutes(nextMinute);
  if (date.getTime() <= Date.now()) date.setMinutes(date.getMinutes() + intervalMinutes);
  return localDateTimeInputValue(date);
};

const parseIds = (value) => (
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((item) => Number(String(item).trim()))
    .filter((item) => Number.isInteger(item) && item > 0)
);

const normalizeSelectIds = (value) => (
  Array.isArray(value) ? value : String(value || "").split(",").filter(Boolean)
);

const clientOptions = (clients) => (
  clients.map((client) => ({ id: Number(client.id), label: displayPersonName(client) }))
);

const resourceName = (item, fallback) => (
  [item?.name, item?.surname].filter(Boolean).join(" ").trim()
  || item?.displayName
  || item?.email
  || item?.name
  || fallback
);

const isSpaAreaName = (name) => /^area\s*\d*/i.test(String(name || "").trim());

const appointmentLabel = (appointment) => (
  [
    `#${appointment.id}`,
    appointment.startTime && appointment.endTime ? `${appointment.startTime} - ${appointment.endTime}` : appointment.date,
    appointment.clientName,
    appointment.treatmentName,
    appointment.assignedResources?.operators?.[0]?.displayName || appointment.assignedResources?.operators?.[0]?.name,
    appointment.assignedResources?.cabins?.[0]?.name,
  ].filter(Boolean).join(" | ")
);

const appointmentStatusLabel = (appointment) => (
  [appointment.operationalStatus, appointment.commercialStatus].filter(Boolean).join(" / ")
);

const appointmentLabelWithStatus = (appointment) => (
  [appointmentLabel(appointment), appointmentStatusLabel(appointment)].filter(Boolean).join(" | ")
);

const packageItemTransactionSets = (item) => {
  const transactions = Array.isArray(item?.transactions) ? item.transactions : [];
  const idsByType = {
    reserve: new Set(),
    release: new Set(),
    consume: new Set(),
  };
  transactions.forEach((transaction) => {
    const appointmentId = Number(transaction.appointmentId);
    if (!appointmentId) return;
    const type = String(transaction.type || "").toUpperCase();
    if (type === "RESERVE") idsByType.reserve.add(appointmentId);
    if (type === "RELEASE") idsByType.release.add(appointmentId);
    if (type === "CONSUME") idsByType.consume.add(appointmentId);
  });
  return idsByType;
};

const hasActivePackageReservation = (item, appointmentId) => {
  const id = Number(appointmentId);
  const ids = packageItemTransactionSets(item);
  return ids.reserve.has(id) && !ids.release.has(id) && !ids.consume.has(id);
};

const isPackageReservationCandidate = (appointment) => (
  !["CANCELLED", "NO_SHOW"].includes(String(appointment?.commercialStatus || appointment?.status || "").toUpperCase())
  && String(appointment?.operationalStatus || "SCHEDULED").toUpperCase() !== "COMPLETED"
);

const isPackageConsumptionCandidate = (appointment, item) => {
  const commercialStatus = String(appointment?.commercialStatus || appointment?.status || "").toUpperCase();
  const operationalStatus = String(appointment?.operationalStatus || "SCHEDULED").toUpperCase();
  return hasActivePackageReservation(item, appointment?.id)
    && (operationalStatus === "COMPLETED" || commercialStatus === "NO_SHOW");
};

const combinationLabel = (combination, operatorOptions, cabinOptions) => {
  const operators = (combination.operatorIds || [])
    .map((id) => operatorOptions.find((item) => item.id === Number(id))?.label || `Operator ${id}`)
    .join(", ");
  const cabins = (combination.cabinIds || [])
    .map((id) => cabinOptions.find((item) => item.id === Number(id))?.label || `Cabin ${id}`)
    .join(", ");
  return [operators, cabins].filter(Boolean).join(" | ");
};

const conflictMessages = {
  START_NOT_ALIGNED: "Start time is not aligned with the planner interval.",
  SPA_CLOSED_DAY: "The spa is closed on this day.",
  SPA_HOLIDAY: "The spa is closed for a holiday.",
  OUTSIDE_OPENING_HOURS: "The appointment is outside spa opening hours.",
  OPERATOR_COUNT_SHORTAGE: "No qualified operator is available for this treatment and time.",
  OPERATOR_NOT_FOUND: "Selected operator was not found.",
  OPERATOR_INACTIVE: "Selected operator is inactive.",
  OPERATOR_NOT_QUALIFIED: "Selected operator is not qualified for this treatment.",
  OPERATOR_OUTSIDE_SHIFT: "Selected operator is outside shift.",
  OPERATOR_SCHEDULE_BLOCKED: "Selected operator is blocked.",
  OPERATOR_APPOINTMENT_CONFLICT: "Selected operator is already booked.",
  OPERATOR_WAITLIST_HOLD_CONFLICT: "Selected operator is held by a waitlist offer.",
  CABIN_REQUIRED: "A compatible cabin is required.",
  CABIN_NOT_FOUND: "Selected cabin was not found.",
  CABIN_INACTIVE: "Selected cabin is inactive.",
  CABIN_INCOMPATIBLE: "Selected cabin is not compatible with this treatment.",
  CABIN_OPERATIONAL_BLOCK: "Selected cabin is blocked.",
  CABIN_APPOINTMENT_CONFLICT: "Selected cabin is already booked.",
  CABIN_WAITLIST_HOLD_CONFLICT: "Selected cabin is held by a waitlist offer.",
  CABIN_CAPACITY_SHORTAGE: "Selected cabin does not have enough capacity.",
  EQUIPMENT_REQUIREMENT_SHORTAGE: "Required equipment is not available.",
  EQUIPMENT_QUANTITY_UNAVAILABLE: "Required equipment quantity is not available.",
  CAPACITY_EXCEEDED: "Spa capacity is full for this time.",
};

const rejectionLabel = (rejection) => {
  const conflicts = Array.isArray(rejection?.conflicts) ? rejection.conflicts : [];
  const conflictText = conflicts.map((conflict) => (
    conflictMessages[conflict.code] || conflict.message || conflict.code
  )).filter(Boolean).join(", ");
  const resourceText = Array.isArray(rejection?.resourceIds) && rejection.resourceIds.length
    ? ` (${rejection.resourceIds.join(", ")})`
    : "";
  const scope = String(rejection?.scope || "CONFLICT").toLowerCase();
  return `${scope}${resourceText}${conflictText ? `: ${conflictText}` : ""}`;
};

const availabilityMessage = (result, fallback = "No available resource combination found") => {
  const rejections = Array.isArray(result?.rejections) ? result.rejections : [];
  const text = rejections.map(rejectionLabel).filter(Boolean).join("\n");
  return text || fallback;
};

const entitlementItemProductId = (item) => (
  Number(item?.productId ?? item?.product?.id ?? item?.treatmentProductId ?? 0) || 0
);

function SpaFinancePage() {
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const { can } = usePermissions();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const ft = useCallback((key, defaultValue, options = {}) => (
    t(`spa.finance.${key}`, { defaultValue, ...options })
  ), [t]);
  const enumLabel = (value) => ft(`enums.${value}`, value);
  const canViewFinance = can("spa-finance:view");
  const canManageFinance = canViewFinance && can("spa-finance:manage");
  const canViewClients = can("spa-clients:view") || can("clients:view");
  const canViewAppointments = canViewFinance || can("spa-appointments:view");
  const canGenerateAppointments = canManageFinance && can("spa-appointments:manage");
  const canViewResources = can("spa-resources:view");

  const [tab, setTab] = useState("packages");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [appointmentError, setAppointmentError] = useState("");
  const [working, setWorking] = useState(false);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [operators, setOperators] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [clientIdFilter, setClientIdFilter] = useState("");
  const [entitlements, setEntitlements] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [instrumentTypeFilter, setInstrumentTypeFilter] = useState("");
  const [instrumentStatusFilter, setInstrumentStatusFilter] = useState("");
  const [entitlementForm, setEntitlementForm] = useState(emptyEntitlementForm);
  const [packageMasterForm, setPackageMasterForm] = useState(emptyPackageMasterForm);
  const [storedValueForm, setStoredValueForm] = useState(emptyStoredValueForm);
  const [storedValueTouched, setStoredValueTouched] = useState(false);
  const [packageAction, setPackageAction] = useState(emptyPackageAction);
  const [storedValueAction, setStoredValueAction] = useState(emptyStoredValueAction);
  const [generation, setGeneration] = useState(emptyAppointmentGeneration);
  const [generationAvailability, setGenerationAvailability] = useState(null);
  const [generationError, setGenerationError] = useState("");
  const [generationChecking, setGenerationChecking] = useState(false);
  const actionLock = useRef(false);
  const loadVersion = useRef(0);
  const sessionKey = JSON.stringify([token, tenant, tenantType, canViewFinance, canViewClients, canViewAppointments]);
  const currentSession = useRef(sessionKey);
  currentSession.current = sessionKey;
  const isValueVoucher = storedValueForm.type === "VALUE_VOUCHER";
  const voucherErrors = storedValueFormErrors(storedValueForm);
  const formDisabled = !canManageFinance || working || loading;

  const clientsForSelect = useMemo(() => clientOptions([...new Map([
    ...entitlements.map((item) => item.client),
    ...instruments.map((item) => item.client),
    ...clients,
  ].filter((client) => client?.id).map((client) => [Number(client.id), client])).values()]), [clients, entitlements, instruments]);
  const packageOptions = useMemo(() => (
    packages.map((item) => ({ id: Number(item.id), label: item.name || `Package ${item.id}` }))
  ), [packages]);
  const treatmentOptions = useMemo(() => (
    treatments.map((item) => ({ id: Number(item.id), label: `${item.name || `Treatment ${item.id}`} - ${item.durationMinutes || 0} min` }))
  ), [treatments]);
  const operatorOptions = useMemo(() => (
    operators.map((item) => ({ id: Number(item.id), label: resourceName(item, `Operator ${item.id}`) }))
  ), [operators]);
  const cabinOptions = useMemo(() => (
    cabins
      .filter((item) => !isSpaAreaName(item.name))
      .map((item) => ({ id: Number(item.id), label: item.name || `Cabin ${item.id}` }))
  ), [cabins]);

  const loadFinanceData = useCallback(async () => {
    const version = ++loadVersion.current;
    const requestKey = sessionKey;
    if (!token || !tenant || tenantType !== "SPA" || !canViewFinance) {
      setInstruments([]);
      setEntitlements([]);
      setClients([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const filters = clientIdFilter ? { clientId: Number(clientIdFilter) } : {};
      const storedValueFilters = {
        ...filters,
        ...(instrumentTypeFilter ? { type: instrumentTypeFilter } : {}),
        ...(instrumentStatusFilter ? { status: instrumentStatusFilter } : {}),
      };
      const results = await Promise.allSettled([
        canViewClients ? getAllClienti(token, tenant) : Promise.resolve([]),
        getAllPackages(token, tenant),
        getSpaTreatments(token, tenant),
        getSpaOperators(token, tenant),
        getSpaCabins(token, tenant),
        getSpaPackageEntitlements(filters, token, tenant),
        getSpaStoredValueInstruments(storedValueFilters, token, tenant),
      ]);
      if (loadVersion.current !== version || currentSession.current !== requestKey) return;
      const [clientData, packageData, treatmentData, operatorData, cabinData, entitlementData, instrumentData] = results.map((result) => (
        result.status === "fulfilled" ? result.value : []
      ));
      const failures = results.filter((result) => result.status === "rejected");
      if (failures.length) setLoadError(failures.map((result) => result.reason?.message || ft("messages.loadError", "Error loading SPA finance")).join("; "));
      const normalizedClients = Array.isArray(clientData) ? clientData : [];
      const normalizedPackages = Array.isArray(packageData) ? packageData : [];
      const normalizedTreatments = Array.isArray(treatmentData) ? treatmentData : [];
      const normalizedOperators = Array.isArray(operatorData) ? operatorData : [];
      const normalizedCabins = Array.isArray(cabinData) ? cabinData : [];
      setClients(normalizedClients);
      setPackages(normalizedPackages);
      setTreatments(normalizedTreatments);
      setOperators(normalizedOperators);
      setCabins(normalizedCabins);
      setEntitlements(Array.isArray(entitlementData) ? entitlementData : []);
      setInstruments(Array.isArray(instrumentData) ? instrumentData : []);
    } catch (error) {
      if (loadVersion.current !== version || currentSession.current !== requestKey) return;
      setClients([]);
      setPackages([]);
      setTreatments([]);
      setOperators([]);
      setCabins([]);
      setEntitlements([]);
      setInstruments([]);
      setLoadError(error?.message || ft("messages.loadError", "Error loading SPA finance"));
    } finally {
      if (loadVersion.current === version && currentSession.current === requestKey) setLoading(false);
    }
  }, [canViewFinance, canViewClients, clientIdFilter, ft, instrumentStatusFilter, instrumentTypeFilter, sessionKey, tenant, tenantType, token]);

  const loadAppointments = useCallback(async () => {
    const requestKey = currentSession.current;
    setAppointments([]);
    setAppointmentError("");
    if (!token || !tenant || tenantType !== "SPA" || !canViewFinance || !canViewAppointments) return;
    try {
      const data = await getSpaAppointments(token, tenant);
      if (currentSession.current === requestKey) setAppointments(Array.isArray(data) ? data : []);
    } catch (error) {
      if (currentSession.current === requestKey) setAppointmentError(error?.message || ft("messages.loadError", "Error loading SPA finance"));
    }
  }, [canViewFinance, canViewAppointments, ft, tenant, tenantType, token]);

  useEffect(() => {
    loadFinanceData();
  }, [loadFinanceData]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const storedValueAppointmentOptions = useMemo(() => {
    const selectedClientId = Number(
      storedValueAction.instrument?.client?.id
      || clientIdFilter
      || 0
    );
    return appointments
      .filter((appointment) => !selectedClientId || Number(appointment.clientId) === selectedClientId)
      .map((appointment) => ({ id: Number(appointment.id), label: appointmentLabelWithStatus(appointment) }));
  }, [appointments, clientIdFilter, storedValueAction.instrument]);

  const packageAppointmentOptions = useMemo(() => {
    const selectedClientId = Number(
      packageAction.entitlement?.client?.id
      || clientIdFilter
      || 0
    );
    const item = packageAction.item;
    return appointments
      .filter((appointment) => !selectedClientId || Number(appointment.clientId) === selectedClientId)
      .filter((appointment) => {
        if (packageAction.kind === "reserve") return isPackageReservationCandidate(appointment);
        if (packageAction.kind === "consume") return isPackageConsumptionCandidate(appointment, item);
        if (packageAction.kind === "release") return hasActivePackageReservation(item, appointment.id);
        return true;
      })
      .map((appointment) => ({ id: Number(appointment.id), label: appointmentLabelWithStatus(appointment) }));
  }, [appointments, clientIdFilter, packageAction.entitlement, packageAction.item, packageAction.kind]);

  const noPackageAppointmentsMessage = useMemo(() => {
    if (packageAction.kind === "consume") {
      return ft("messages.noConsumablePackageAppointments", "No completed or no-show appointment has an active package reservation for this client");
    }
    if (packageAction.kind === "release") {
      return ft("messages.noReleasablePackageAppointments", "No appointment has an active package reservation for this client");
    }
    return ft("messages.noClientAppointments", "No appointments found for this client");
  }, [ft, packageAction.kind]);

  const runAction = async (action, successMessage = ft("messages.saved", "Saved")) => {
    if (!canManageFinance || actionLock.current || !token || !tenant || tenantType !== "SPA") return;
    actionLock.current = true;
    setWorking(true);
    try {
      await action();
      await Promise.all([loadFinanceData(), loadAppointments()]);
      showSnackbar(successMessage, "success");
    } catch (error) {
      showSnackbar(error?.message || ft("messages.saveError", "Error saving finance operation"), "error");
    } finally {
      actionLock.current = false;
      setWorking(false);
    }
  };

  const saveEntitlement = () => {
    const selectedPackage = packages.find((item) => Number(item.id) === Number(entitlementForm.packageId));
    if (!selectedPackage) {
      showSnackbar(ft("messages.selectPackageFirst", "Create or select a package before assigning it"), "warning");
      return;
    }

    runAction(async () => {
      await createSpaPackageEntitlement({
        clientId: Number(entitlementForm.clientId),
        packageId: Number(entitlementForm.packageId),
        quantity: Number(entitlementForm.quantity || 1),
        ...(entitlementForm.validFrom ? { validFrom: localInputToOffset(entitlementForm.validFrom) } : {}),
        expiresAt: entitlementForm.expiresAt ? localInputToOffset(entitlementForm.expiresAt) : null,
      }, token, tenant);
      setEntitlementForm(emptyEntitlementForm);
    }, ft("messages.packageAssigned", "Package assigned"));
  };

  const savePackageMaster = () => runAction(async () => {
    const payload = {
      name: packageMasterForm.name.trim(),
      description: packageMasterForm.description.trim(),
      price: Number(packageMasterForm.price || 0),
      type: "STANDARD",
      items: [{
        productId: Number(packageMasterForm.treatmentProductId),
        variantId: null,
        quantity: Number(packageMasterForm.includedQuantity || 1),
        courseIndex: 1,
        courseName: null,
        sortOrder: 0,
      }],
      visibility: [],
    };

    if (packageMasterForm.id) {
      await updatePackage(packageMasterForm.id, payload, token, tenant);
    } else {
      await createPackage(payload, token, tenant);
    }
    setPackageMasterForm(emptyPackageMasterForm);
  }, packageMasterForm.id ? ft("messages.packageUpdated", "Package updated") : ft("messages.packageCreated", "Package created"));

  const editPackageMaster = (pkg) => {
    const firstItem = Array.isArray(pkg.items) ? pkg.items[0] : null;
    setPackageMasterForm({
      id: pkg.id,
      name: pkg.name || "",
      description: pkg.description || "",
      price: pkg.price ?? "",
      treatmentProductId: firstItem?.productId ? String(firstItem.productId) : "",
      includedQuantity: firstItem?.quantity || 1,
    });
  };

  const deletePackageMaster = (packageId) => {
    if (!window.confirm(ft("confirm.deletePackage", "Delete this package?"))) return;
    runAction(async () => {
      await deletePackageById(packageId, token, tenant);
      if (Number(packageMasterForm.id) === Number(packageId)) {
        setPackageMasterForm(emptyPackageMasterForm);
      }
    }, ft("messages.packageDeleted", "Package deleted"));
  };

  const saveStoredValue = () => {
    setStoredValueTouched(true);
    if (loading || !canManageFinance || Object.keys(storedValueFormErrors(storedValueForm)).length) return;
    return runAction(async () => {
      await createSpaStoredValueInstrument({
        clientId: Number(storedValueForm.clientId),
        type: storedValueForm.type,
        vatType: storedValueForm.vatType,
        initialValue: Number(storedValueForm.initialValue),
        ...(isValueVoucher ? {
          motivation: storedValueForm.motivation.trim(),
          ...(storedValueForm.authorization.trim() ? { authorization: storedValueForm.authorization.trim() } : {}),
        } : {}),
        expiresAt: storedValueForm.expiresAt ? localInputToOffset(storedValueForm.expiresAt) : null,
      }, token, tenant);
      setStoredValueForm(emptyStoredValueForm);
      setStoredValueTouched(false);
    }, ft("messages.storedValueCreated", "Stored value created"));
  };

  const submitPackageAction = () => runAction(async () => {
    if (packageAction.kind === "consume") {
      const appointment = appointments.find((item) => Number(item.id) === Number(packageAction.appointmentId));
      if (!isPackageConsumptionCandidate(appointment, packageAction.item)) {
        throw new Error(ft("messages.consumeRequiresClosedReservedAppointment", "Select a completed or no-show appointment with an active package reservation"));
      }
    }
    if (packageAction.kind === "reserve") {
      await reserveSpaPackageCredit(packageAction.item.id, {
        appointmentId: Number(packageAction.appointmentId),
        quantity: Number(packageAction.quantity || 1),
      }, token, tenant);
    } else if (packageAction.kind === "consume") {
      await consumeSpaPackageCredit(packageAction.item.id, {
        appointmentId: Number(packageAction.appointmentId),
      }, token, tenant);
    } else if (packageAction.kind === "release") {
      await releaseSpaPackageCredit(packageAction.item.id, {
        appointmentId: Number(packageAction.appointmentId),
        ...(packageAction.reason ? { reason: packageAction.reason } : {}),
      }, token, tenant);
    } else if (packageAction.kind === "reverse") {
      await reverseSpaPackageTransaction(packageAction.transaction.id, {
        reason: packageAction.reason,
      }, token, tenant);
    }
    setPackageAction(emptyPackageAction);
  }, ft("messages.packageCreditUpdated", "Package credit updated"));

  const submitStoredValueAction = () => runAction(async () => {
    if (["reserve", "consume", "release"].includes(storedValueAction.kind)) {
      const appointment = appointments.find((item) => Number(item.id) === Number(storedValueAction.appointmentId));
      if (!canViewAppointments || !appointment || Number(appointment.clientId) !== Number(storedValueAction.instrument?.client?.id)) {
        throw new Error(ft("storedValue.validation.clientAppointment", "Select an appointment belonging to this customer."));
      }
    }
    if (["reserve", "consume"].includes(storedValueAction.kind) && !isPositiveMoney(storedValueAction.amount)) {
      throw new Error(ft("storedValue.validation.invalidAmount", "Enter a positive amount with up to two decimal places."));
    }
    if (storedValueAction.kind === "reserve") {
      await reserveSpaStoredValue(storedValueAction.instrument.id, {
        appointmentId: Number(storedValueAction.appointmentId),
        amount: Number(storedValueAction.amount),
      }, token, tenant);
    } else if (storedValueAction.kind === "consume") {
      await consumeSpaStoredValue(storedValueAction.instrument.id, {
        appointmentId: Number(storedValueAction.appointmentId),
        amount: Number(storedValueAction.amount),
      }, token, tenant);
    } else if (storedValueAction.kind === "release") {
      await releaseSpaStoredValue(storedValueAction.instrument.id, {
        appointmentId: Number(storedValueAction.appointmentId),
        ...(storedValueAction.reason ? { reason: storedValueAction.reason } : {}),
      }, token, tenant);
    } else if (storedValueAction.kind === "reverse") {
      await reverseSpaStoredValueTransaction(storedValueAction.transaction.id, {
        reason: storedValueAction.reason,
      }, token, tenant);
    } else if (storedValueAction.kind === "status") {
      await changeSpaStoredValueStatus(storedValueAction.instrument.id, {
        status: storedValueAction.status,
        ...(storedValueAction.reason ? { reason: storedValueAction.reason } : {}),
      }, token, tenant);
    }
    setStoredValueAction(emptyStoredValueAction);
  }, ft("messages.storedValueUpdated", "Stored value updated"));

  const generationAvailabilityPayload = () => ({
    treatmentProductId: entitlementItemProductId(generation.item),
    startAt: localInputToOffset(generation.startAt),
    partySize: 1,
    preferredResources: {
      operatorIds: parseIds(generation.operatorIds),
      cabinIds: parseIds(generation.cabinIds),
      equipmentIds: [],
    },
  });

  const fetchGenerationAvailability = async () => {
    const result = await checkSpaAppointmentAvailability(generationAvailabilityPayload(), token, tenant);
    setGenerationAvailability(result);
    const firstCombination = Array.isArray(result?.combinations) ? result.combinations[0] : null;
    setGeneration((current) => ({ ...current, selectedCombination: firstCombination || null }));
    const message = result?.available
      ? ft("messages.availableCombinations", "Available resource combinations found")
      : availabilityMessage(result);
    setGenerationError(result?.available ? "" : message);
    return { result, firstCombination, message };
  };

  const submitAppointmentGeneration = () => runAction(async () => {
    const productId = entitlementItemProductId(generation.item);
    if (!productId) throw new Error(ft("messages.missingTreatmentProduct", "Package item is missing SPA treatment product."));
    if (!generation.startAt) throw new Error(ft("messages.selectStartTime", "Select appointment start time."));
    setGenerationError("");
    const { firstCombination, message } = generation.selectedCombination
      ? { firstCombination: generation.selectedCombination, message: "" }
      : await fetchGenerationAvailability();
    if (!firstCombination) {
      throw new Error(message || ft("messages.noCombination", "No available resource combination found"));
    }
    await generateSpaPackageAppointments(generation.entitlement.id, {
      appointments: [{
        entitlementItemId: Number(generation.item.id),
        startAt: localInputToOffset(generation.startAt),
        customerType: generation.customerType,
        commercialStatus: generation.commercialStatus,
        preferredResources: {
          operatorIds: parseIds(generation.operatorIds),
          cabinIds: parseIds(generation.cabinIds),
          equipmentIds: [],
        },
        resources: firstCombination,
      }],
    }, token, tenant);
    setGeneration(emptyAppointmentGeneration);
    setGenerationAvailability(null);
    setGenerationError("");
  }, ft("messages.packageAppointmentGenerated", "Package appointment generated"));

  const openAppointmentGeneration = (entitlement, item) => {
    setGenerationAvailability(null);
    setGenerationError("");
    setGeneration({ ...emptyAppointmentGeneration, startAt: nextPlannerSlotInput(), entitlement, item });
  };

  const checkGenerationAvailability = async () => {
    const productId = entitlementItemProductId(generation.item);
    if (!productId) {
      setGenerationError(ft("messages.missingTreatmentProduct", "Package item is missing SPA treatment product."));
      return;
    }
    if (!generation.startAt) {
      setGenerationError(ft("messages.selectStartTime", "Select appointment start time."));
      return;
    }
    setGenerationChecking(true);
    setGenerationError("");
    try {
      const { result, message } = await fetchGenerationAvailability();
      showSnackbar(
        result?.available ? ft("messages.availableCombinations", "Available resource combinations found") : message,
        result?.available ? "success" : "warning"
      );
    } catch (error) {
      setGenerationAvailability(null);
      setGeneration((current) => ({ ...current, selectedCombination: null }));
      const message = availabilityMessage(error?.payload, error?.message || "Error checking availability");
      setGenerationError(message);
      showSnackbar(message, "error");
    } finally {
      setGenerationChecking(false);
    }
  };

  if (tenantType !== "SPA") {
    return (
      <SidebarLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">{t("spa.common.spaOnly", { defaultValue: "SPA finance is available only for SPA tenants." })}</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  if (!canViewFinance) {
    return (
      <SidebarLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">{ft("messages.noPermission", "You do not have permission to view SPA finance.")}</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: "auto" }}>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" fontWeight={900}>{ft("title", "SPA finance")}</Typography>
              <Typography color="text.secondary">{ft("subtitle", "Packages, credits, gift cards, vouchers, and internal credit.")}</Typography>
            </Box>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={loadFinanceData} disabled={loading || working}>
              {t("common.actions.refresh", { defaultValue: "Refresh" })}
            </Button>
          </Stack>

          {loadError && <Alert severity="error">{ft("messages.loadError", "Error loading SPA finance")}: {loadError}</Alert>}
          {appointmentError && <Alert severity="warning">{appointmentError}</Alert>}

          {["packages", "stored-value"].includes(tab) ? <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small" disabled={working || loading}>
                  <InputLabel id="finance-client-filter">{ft("filters.client", "Client filter")}</InputLabel>
                  <Select labelId="finance-client-filter" label={ft("filters.client", "Client filter")} value={clientIdFilter} onChange={(event) => setClientIdFilter(event.target.value)}>
                    <MenuItem value="">{ft("filters.allClients", "All clients")}</MenuItem>
                    {clientsForSelect.map((client) => <MenuItem key={client.id} value={String(client.id)}>{client.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Alert severity="info">
                  {ft("permissions.generateAppointment", "Appointment generation from packages also requires")} <strong>spa-appointments:manage</strong>.
                </Alert>
              </Grid>
            </Grid>
          </Paper> : null}

          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth"
            sx={{ "& .MuiTab-root": { flex: { xs: "1 1 0", sm: "0 1 auto" }, minWidth: 0, maxWidth: "25%", minHeight: { xs: 72, sm: 48 } } }}>
            <Tab value="packages" icon={<InventoryIcon />} iconPosition="start" label={ft("tabs.packages", "Purchased packages")} />
            <Tab value="stored-value" icon={<CreditCardIcon />} iconPosition="start" label={ft("tabs.storedValue", "Gift cards & credits")} />
            <Tab value="retail" icon={<PointOfSaleIcon />} iconPosition="start" label={ft("tabs.retail", "Retail sales")} />
            <Tab value="commissions" icon={<PercentIcon />} iconPosition="start" label={ft("tabs.commissions", "Operator compensation")} />
          </Tabs>

          {loading ? (
            <Alert severity="info" icon={<CircularProgress size={18} />}>{ft("messages.loading", "Loading SPA finance...")}</Alert>
          ) : tab === "commissions" ? (
            <SpaCommissionManager token={token} tenant={tenant} canManage={canManageFinance} />
          ) : tab === "retail" ? (
            <SpaRetailSales token={token} tenant={tenant} canManage={canManageFinance} canViewResources={canViewResources}
              clients={clientsForSelect} operators={operatorOptions} />
          ) : tab === "packages" ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={850}>{ft("packages.masterData", "Package master data")}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ft("packages.masterDataHelp", "Uses shared /api/packages. Package items use productId from SPA treatments.")}
                      </Typography>
                    </Box>
                    {packageMasterForm.id ? (
                      <Chip color="warning" label={ft("packages.editing", "Editing package #{{id}}", { id: packageMasterForm.id })} />
                    ) : (
                      <Chip color="info" label={ft("packages.sharedCatalog", "Shared package catalog")} />
                    )}
                  </Stack>

                  {treatments.length === 0 ? (
                    <Alert severity="warning">
                      {ft("packages.noTreatments", "No SPA treatments found. Create at least one treatment before creating a package.")}
                    </Alert>
                  ) : null}

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label={ft("packages.name", "Package name")}
                        value={packageMasterForm.name}
                        onChange={(event) => setPackageMasterForm((current) => ({ ...current, name: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t("spa.fields.notes", { defaultValue: "Description" })}
                        value={packageMasterForm.description}
                        onChange={(event) => setPackageMasterForm((current) => ({ ...current, description: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t("spa.fields.price", { defaultValue: "Price" })}
                        type="number"
                        value={packageMasterForm.price}
                        onChange={(event) => setPackageMasterForm((current) => ({ ...current, price: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{ft("packages.includedTreatment", "Included treatment")}</InputLabel>
                        <Select
                          label={ft("packages.includedTreatment", "Included treatment")}
                          value={packageMasterForm.treatmentProductId}
                          onChange={(event) => setPackageMasterForm((current) => ({ ...current, treatmentProductId: event.target.value }))}
                        >
                          {treatmentOptions.map((treatment) => (
                            <MenuItem key={treatment.id} value={String(treatment.id)}>{treatment.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label={ft("packages.qty", "Qty")}
                        type="number"
                        value={packageMasterForm.includedQuantity}
                        onChange={(event) => setPackageMasterForm((current) => ({ ...current, includedQuantity: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          startIcon={<AddIcon />}
                          variant="contained"
                          onClick={savePackageMaster}
                          disabled={!canManageFinance || working || !packageMasterForm.name.trim() || !packageMasterForm.treatmentProductId || Number(packageMasterForm.includedQuantity) <= 0}
                        >
                          {packageMasterForm.id ? ft("packages.update", "Update package") : ft("packages.create", "Create package")}
                        </Button>
                        {packageMasterForm.id ? (
                          <Button variant="outlined" onClick={() => setPackageMasterForm(emptyPackageMasterForm)} disabled={working}>
                            {ft("packages.cancelEdit", "Cancel edit")}
                          </Button>
                        ) : null}
                      </Stack>
                    </Grid>
                  </Grid>

                  {packages.length === 0 ? (
                    <Alert severity="info">
                      {ft("packages.emptyCreateFirst", "No packages found. Create a package here first, then assign it to a client below.")}
                    </Alert>
                  ) : (
                    <Grid container spacing={1.5}>
                      {packages.map((pkg) => (
                        <Grid item xs={12} md={6} key={pkg.id}>
                          <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                            <Stack spacing={1}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Box>
                                  <Typography fontWeight={850}>{pkg.name || ft("packages.fallbackName", "Package {{id}}", { id: pkg.id })}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {money(pkg.price)} | {pkg.type || "STANDARD"}
                                  </Typography>
                                </Box>
                                <Chip size="small" label={`#${pkg.id}`} />
                              </Stack>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {(pkg.items || []).map((item) => (
                                  <Chip
                                    key={item.id || `${pkg.id}-${item.productId}`}
                                    size="small"
                                    label={`${item.product?.name || ft("packages.productFallback", "Product {{id}}", { id: item.productId })} x${item.quantity}`}
                                  />
                                ))}
                              </Stack>
                              <Stack direction="row" spacing={0.75}>
                                <Button size="small" variant="outlined" disabled={!canManageFinance || working} onClick={() => editPackageMaster(pkg)}>
                                  {t("common.actions.edit", { defaultValue: "Edit" })}
                                </Button>
                                <Button size="small" color="error" variant="outlined" disabled={!canManageFinance || working} onClick={() => deletePackageMaster(pkg.id)}>
                                  {t("common.actions.delete", { defaultValue: "Delete" })}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={850}>{ft("packages.assignToClient", "Assign package to client")}</Typography>
                  {packages.length === 0 ? (
                    <Alert severity="info">
                      {ft("packages.emptyBeforeAssign", "The package list is empty. Create a package above before assigning package credits.")}
                    </Alert>
                  ) : null}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t("spa.fields.client", { defaultValue: "Client" })}</InputLabel>
                        <Select label={t("spa.fields.client", { defaultValue: "Client" })} value={entitlementForm.clientId} onChange={(event) => setEntitlementForm((current) => ({ ...current, clientId: event.target.value }))}>
                          {clientsForSelect.map((client) => <MenuItem key={client.id} value={String(client.id)}>{client.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{ft("packages.package", "Package")}</InputLabel>
                        <Select label={ft("packages.package", "Package")} value={entitlementForm.packageId} onChange={(event) => setEntitlementForm((current) => ({ ...current, packageId: event.target.value }))}>
                          {packageOptions.length === 0 ? <MenuItem disabled>{ft("packages.noneAvailable", "No packages available")}</MenuItem> : null}
                          {packageOptions.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField fullWidth size="small" label={t("spa.fields.quantity", { defaultValue: "Quantity" })} type="number" value={entitlementForm.quantity} onChange={(event) => setEntitlementForm((current) => ({ ...current, quantity: event.target.value }))} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField fullWidth size="small" label={ft("fields.validFrom", "Valid from")} type="datetime-local" InputLabelProps={{ shrink: true }} value={entitlementForm.validFrom} onChange={(event) => setEntitlementForm((current) => ({ ...current, validFrom: event.target.value }))} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField fullWidth size="small" label={ft("fields.expiresAt", "Expires at")} type="datetime-local" InputLabelProps={{ shrink: true }} value={entitlementForm.expiresAt} onChange={(event) => setEntitlementForm((current) => ({ ...current, expiresAt: event.target.value }))} />
                    </Grid>
                    <Grid item xs={12}>
                      <Button startIcon={<AddIcon />} variant="contained" onClick={saveEntitlement} disabled={!canManageFinance || working || !entitlementForm.clientId || !entitlementForm.packageId || Number(entitlementForm.quantity) <= 0}>
                        {ft("packages.assign", "Assign package")}
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              {entitlements.length === 0 ? <Alert severity="info">{ft("packages.noEntitlements", "No package entitlements found.")}</Alert> : entitlements.map((entitlement) => (
                <Paper key={entitlement.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="h6" fontWeight={900}>{entitlement.package?.name || ft("packages.fallbackName", "Package {{id}}", { id: entitlement.id })}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {displayPersonName(entitlement.client)} | {ft("packages.qty", "Qty")} {entitlement.purchaseQuantity} | {compactDateLabel(entitlement.validFrom)} - {compactDateLabel(entitlement.expiresAt)}
                        </Typography>
                      </Box>
                      <Chip label={entitlement.status} color={entitlement.status === "ACTIVE" ? "success" : "default"} />
                    </Stack>
                    <Grid container spacing={1.5}>
                      {(entitlement.items || []).map((item) => (
                        <Grid item xs={12} md={6} key={item.id}>
                          <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                            <Stack spacing={1}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Box>
                                  <Typography fontWeight={850}>{item.product?.name || ft("packages.itemFallback", "Item {{id}}", { id: item.id })}</Typography>
                                  <Typography variant="caption" color="text.secondary">{item.serviceType}</Typography>
                                </Box>
                                <Chip size="small" label={ft("packages.remaining", "Remaining {{count}}", { count: item.remainingQuantity })} color={item.remainingQuantity > 0 ? "success" : "default"} />
                              </Stack>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={ft("packages.total", "Total {{count}}", { count: item.totalQuantity })} />
                                <Chip size="small" label={ft("packages.reserved", "Reserved {{count}}", { count: item.reservedQuantity })} />
                                <Chip size="small" label={ft("packages.consumed", "Consumed {{count}}", { count: item.consumedQuantity })} />
                              </Stack>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Button size="small" variant="outlined" disabled={!canManageFinance || working} onClick={() => setPackageAction({ ...emptyPackageAction, kind: "reserve", item, entitlement })}>{ft("actions.reserve", "Reserve")}</Button>
                                <Button size="small" variant="outlined" disabled={!canManageFinance || working || item.reservedQuantity <= 0} onClick={() => setPackageAction({ ...emptyPackageAction, kind: "consume", item, entitlement })}>{ft("actions.consume", "Consume")}</Button>
                                <Button size="small" variant="outlined" disabled={!canManageFinance || working || item.reservedQuantity <= 0} onClick={() => setPackageAction({ ...emptyPackageAction, kind: "release", item, entitlement })}>{ft("actions.release", "Release")}</Button>
                                <Button size="small" variant="contained" disabled={!canGenerateAppointments || working || item.serviceType !== "SPA_TREATMENT" || item.remainingQuantity <= 0} onClick={() => openAppointmentGeneration(entitlement, item)}>{ft("actions.generateAppointment", "Generate appointment")}</Button>
                              </Stack>
                              {(item.transactions || []).length ? (
                                <Stack spacing={0.5}>
                                  {(item.transactions || []).slice(-3).map((transaction) => (
                                    <Stack key={transaction.id} direction="row" spacing={1} alignItems="center">
                                      <Chip size="small" label={`${enumLabel(transaction.type)} #${transaction.id}`} />
                                      <Typography variant="caption" color="text.secondary">{ft("fields.appointment", "Appointment")} {transaction.appointmentId || "-"} | {dateLabel(transaction.createdAt)}</Typography>
                                      {transaction.type === "CONSUME" ? (
                                        <Button size="small" disabled={!canManageFinance || working} onClick={() => setPackageAction({ ...emptyPackageAction, kind: "reverse", transaction })}>{ft("actions.reverse", "Reverse")}</Button>
                                      ) : null}
                                    </Stack>
                                  ))}
                                </Stack>
                              ) : null}
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }} component="form" aria-label={ft("storedValue.issueTitle", "Issue gift card, voucher or credit")}
                onSubmit={(event) => { event.preventDefault(); saveStoredValue(); }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={850}>{ft("storedValue.issueTitle", "Issue stored-value instrument")}</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small" required disabled={formDisabled}>
                        <InputLabel id="stored-value-client">{t("spa.fields.client", { defaultValue: "Client" })}</InputLabel>
                        <Select labelId="stored-value-client" label={t("spa.fields.client", { defaultValue: "Client" })} value={storedValueForm.clientId} onChange={(event) => setStoredValueForm((current) => ({ ...current, clientId: event.target.value }))}>
                          {clientsForSelect.map((client) => <MenuItem key={client.id} value={String(client.id)}>{client.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" disabled={formDisabled}>
                        <InputLabel id="stored-value-type">{t("spa.fields.type", { defaultValue: "Type" })}</InputLabel>
                        <Select labelId="stored-value-type" label={t("spa.fields.type", { defaultValue: "Type" })} value={storedValueForm.type} onChange={(event) => { setStoredValueTouched(false); setStoredValueForm((current) => ({ ...current, type: event.target.value })); }}>
                          {["GIFT_CARD", "VALUE_VOUCHER", "INTERNAL_CREDIT"].map((value) => <MenuItem key={value} value={value}>{enumLabel(value)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" disabled={formDisabled}>
                        <InputLabel id="stored-value-vat">{t("spa.fields.vat", { defaultValue: "VAT" })}</InputLabel>
                        <Select labelId="stored-value-vat" label={t("spa.fields.vat", { defaultValue: "VAT" })} value={storedValueForm.vatType} onChange={(event) => setStoredValueForm((current) => ({ ...current, vatType: event.target.value }))}>
                          {["SINGLE_PURPOSE", "MULTI_PURPOSE"].map((value) => <MenuItem key={value} value={value}>{enumLabel(value)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField fullWidth size="small" required disabled={formDisabled} label={ft("storedValue.initialValue", "Initial value")} type="number" inputProps={{ min: 0.01, max: 9999999999.99, step: 0.01 }} value={storedValueForm.initialValue}
                        error={Boolean(voucherErrors.initialValue && (storedValueTouched || storedValueForm.initialValue))}
                        helperText={voucherErrors.initialValue && (storedValueTouched || storedValueForm.initialValue) ? ft(`storedValue.validation.${voucherErrors.initialValue}`) : ""}
                        onChange={(event) => setStoredValueForm((current) => ({ ...current, initialValue: event.target.value }))} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" disabled={formDisabled} required={isValueVoucher} label={ft("fields.expiresAt", "Expires at")} type="datetime-local" InputLabelProps={{ shrink: true }} value={storedValueForm.expiresAt}
                        error={Boolean(voucherErrors.expiresAt && (storedValueTouched || storedValueForm.expiresAt))}
                        helperText={voucherErrors.expiresAt && (storedValueTouched || storedValueForm.expiresAt) ? ft(`storedValue.validation.${voucherErrors.expiresAt}`) : ""}
                        onChange={(event) => setStoredValueForm((current) => ({ ...current, expiresAt: event.target.value }))} />
                    </Grid>
                    {isValueVoucher && <>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="small" required multiline minRows={2} label={ft("storedValue.motivation", "Motivation")} value={storedValueForm.motivation} disabled={formDisabled}
                          inputProps={{ maxLength: 500 }} onBlur={() => setStoredValueTouched(true)} error={Boolean(storedValueTouched && voucherErrors.motivation)}
                          helperText={storedValueTouched && voucherErrors.motivation ? ft(`storedValue.validation.${voucherErrors.motivation}`) : ""}
                          onChange={(event) => setStoredValueForm((current) => ({ ...current, motivation: event.target.value }))} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="small" multiline minRows={2} label={ft("storedValue.authorization", "Authorization reference (optional)")} value={storedValueForm.authorization} disabled={formDisabled}
                          inputProps={{ maxLength: 500 }} onChange={(event) => setStoredValueForm((current) => ({ ...current, authorization: event.target.value }))} />
                      </Grid>
                    </>}
                    {!canViewClients && <Grid item xs={12}><Alert severity="info">{ft("storedValue.customerAccess", "Customer registry access is required to select additional customers.")}</Alert></Grid>}
                    <Grid item xs={12}>
                      <Button startIcon={<AddIcon />} variant="contained" type="submit" disabled={formDisabled || Object.keys(voucherErrors).length > 0}>
                        {ft("storedValue.issue", "Issue instrument")}
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small" disabled={working || loading}>
                      <InputLabel id="stored-value-type-filter">{ft("filters.type", "Type filter")}</InputLabel>
                      <Select labelId="stored-value-type-filter" label={ft("filters.type", "Type filter")} value={instrumentTypeFilter} onChange={(event) => setInstrumentTypeFilter(event.target.value)}>
                        <MenuItem value="">{t("spa.common.all", { defaultValue: "All" })}</MenuItem>
                        {["GIFT_CARD", "VALUE_VOUCHER", "INTERNAL_CREDIT"].map((value) => <MenuItem key={value} value={value}>{enumLabel(value)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small" disabled={working || loading}>
                      <InputLabel id="stored-value-status-filter">{ft("filters.status", "Status filter")}</InputLabel>
                      <Select labelId="stored-value-status-filter" label={ft("filters.status", "Status filter")} value={instrumentStatusFilter} onChange={(event) => setInstrumentStatusFilter(event.target.value)}>
                        <MenuItem value="">{t("spa.common.all", { defaultValue: "All" })}</MenuItem>
                        {["ACTIVE", "BLOCKED", "CANCELLED", "EXPIRED"].map((value) => <MenuItem key={value} value={value}>{enumLabel(value)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {instruments.length === 0 ? <Alert severity="info">{ft("storedValue.empty", "No stored-value instruments found.")}</Alert> : (
                <Grid container spacing={1.5}>
                  {instruments.map((instrument) => (
                    <Grid item xs={12} md={6} key={instrument.id}>
                      <Paper component="article" aria-label={instrument.code || `#${instrument.id}`} variant="outlined" sx={{ p: 2, height: "100%" }}>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems="flex-start">
                            <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                              <Typography variant="subtitle1" fontWeight={800}>{instrument.code || `#${instrument.id}`}</Typography>
                              <Typography variant="body2" color="text.secondary">{displayPersonName(instrument.client)} | {enumLabel(instrument.type)} | {enumLabel(instrument.vatType)}</Typography>
                            </Box>
                            <Chip label={enumLabel(instrument.status)} color={instrument.status === "ACTIVE" ? "success" : instrument.status === "BLOCKED" ? "warning" : "default"} />
                          </Stack>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={ft("storedValue.initial", "Initial {{amount}}", { amount: money(instrument.initialValue) })} />
                            <Chip size="small" label={ft("storedValue.reserved", "Reserved {{amount}}", { amount: money(instrument.reservedValue) })} />
                            <Chip size="small" label={ft("storedValue.consumed", "Consumed {{amount}}", { amount: money(instrument.consumedValue) })} />
                            <Chip size="small" label={ft("storedValue.remaining", "Remaining balance {{amount}}", { amount: money(instrument.remainingBalance ?? Number(instrument.initialValue) - Number(instrument.consumedValue)) })} />
                            <Chip size="small" color="success" label={ft("storedValue.available", "Available {{amount}}", { amount: money(instrument.availableValue) })} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{ft("fields.expires", "Expires")} {compactDateLabel(instrument.expiresAt)}</Typography>
                          {instrument.type === "VALUE_VOUCHER" && <Box sx={{ overflowWrap: "anywhere" }}>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{ft("storedValue.motivation", "Motivation")}: {instrument.motivation || "-"}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{ft("storedValue.authorization", "Authorization reference (optional)")}: {instrument.authorization || "-"}</Typography>
                            <Typography variant="body2">{ft("storedValue.issuedBy", "Issued by")}: {instrument.issuedBy ? displayPersonName(instrument.issuedBy) : "-"}</Typography>
                            <Typography variant="caption" color="text.secondary">{ft("storedValue.issuedAt", "Issued at")}: {dateLabel(instrument.createdAt)}</Typography>
                          </Box>}
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Button size="small" variant="outlined" disabled={!canManageFinance || !canViewAppointments || working || instrument.status !== "ACTIVE" || Number(instrument.availableValue) <= 0} onClick={() => setStoredValueAction({ ...emptyStoredValueAction, kind: "reserve", instrument })}>{ft("actions.reserve", "Reserve")}</Button>
                            <Button size="small" variant="outlined" disabled={!canManageFinance || !canViewAppointments || working || ["BLOCKED", "CANCELLED"].includes(instrument.status) || Number(instrument.reservedValue) <= 0} onClick={() => setStoredValueAction({ ...emptyStoredValueAction, kind: "consume", instrument })}>{ft("actions.consume", "Consume")}</Button>
                            <Button size="small" variant="outlined" disabled={!canManageFinance || !canViewAppointments || working || Number(instrument.reservedValue) <= 0} onClick={() => setStoredValueAction({ ...emptyStoredValueAction, kind: "release", instrument })}>{ft("actions.release", "Release")}</Button>
                            <Button size="small" variant="outlined" disabled={!canManageFinance || working} onClick={() => setStoredValueAction({ ...emptyStoredValueAction, kind: "status", instrument, status: instrument.status === "BLOCKED" ? "ACTIVE" : "BLOCKED" })}>{t("spa.fields.status", { defaultValue: "Status" })}</Button>
                          </Stack>
                          <Accordion disableGutters elevation={0} sx={{ borderTop: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2" fontWeight={700}>{ft("storedValue.history", "Transaction history")} ({(instrument.transactions || []).length})</Typography></AccordionSummary>
                            <AccordionDetails sx={{ px: 0 }}>
                            <Stack spacing={1.5}>
                            {!(instrument.transactions || []).length && <Typography variant="body2">{ft("storedValue.noHistory", "No transactions recorded.")}</Typography>}
                            {(instrument.transactions || []).map((transaction) => (
                            <Stack key={transaction.id} spacing={0.5} sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1, overflowWrap: "anywhere" }}>
                              <Chip size="small" label={`${enumLabel(transaction.type)} #${transaction.id}`} />
                              <Typography variant="caption" color="text.secondary">{transaction.amount == null ? "-" : money(transaction.amount)} | {ft("fields.appointment", "Appointment")} {transaction.appointmentId || "-"} | {dateLabel(transaction.createdAt)}</Typography>
                              {transaction.createdBy && <Typography variant="caption">{ft("storedValue.author", "Author")}: {displayPersonName(transaction.createdBy)}</Typography>}
                              {transaction.reason && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{ft("fields.reason", "Reason")}: {transaction.reason}</Typography>}
                              {transaction.reversesTransactionId && <Typography variant="caption">{ft("storedValue.reverses", "Reverses transaction")}: #{transaction.reversesTransactionId}</Typography>}
                              {transaction.type === "CONSUME" ? (
                                <Button size="small" disabled={!canManageFinance || working} onClick={() => setStoredValueAction({ ...emptyStoredValueAction, kind: "reverse", transaction })}>{ft("actions.reverse", "Reverse")}</Button>
                              ) : null}
                            </Stack>
                          ))}
                            </Stack>
                            </AccordionDetails>
                          </Accordion>
                          {instrument.type === "VALUE_VOUCHER" && <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2" fontWeight={700}>{ft("storedValue.uses", "Voucher uses")} ({(instrument.uses || []).length})</Typography></AccordionSummary>
                            <AccordionDetails sx={{ px: 0 }}>
                              <Stack spacing={1.5}>
                                {!(instrument.uses || []).length && <Typography variant="body2">{ft("storedValue.noUses", "No voucher uses recorded.")}</Typography>}
                                {(instrument.uses || []).map((use) => <Box key={use.id} sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1, overflowWrap: "anywhere" }}>
                                  <Typography variant="body2">#{use.id} | {ft("fields.appointment", "Appointment")} {use.appointmentId} | {dateLabel(use.usedAt)}</Typography>
                                  <Typography variant="body2">{ft("storedValue.originalUse", "Original amount")}: {money(use.amount)} | {ft("storedValue.reversedUse", "Reversed amount")}: {money(use.reversedAmount)} | {ft("storedValue.netUse", "Net used")}: {money(use.netAmount)}</Typography>
                                  {use.createdBy && <Typography variant="caption">{ft("storedValue.author", "Author")}: {displayPersonName(use.createdBy)}</Typography>}
                                </Box>)}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      <Dialog open={Boolean(packageAction.kind)} onClose={() => setPackageAction(emptyPackageAction)} fullWidth maxWidth="sm">
        <DialogTitle>{ft("dialogs.packageCreditAction", "Package credit action")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {packageAction.kind === "reverse" ? (
              <TextField label={ft("fields.reason", "Reason")} value={packageAction.reason} onChange={(event) => setPackageAction((current) => ({ ...current, reason: event.target.value }))} fullWidth />
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>{ft("fields.appointment", "Appointment")}</InputLabel>
                  <Select label={ft("fields.appointment", "Appointment")} value={packageAction.appointmentId} onChange={(event) => setPackageAction((current) => ({ ...current, appointmentId: event.target.value }))}>
                    {packageAppointmentOptions.length ? packageAppointmentOptions.map((appointment) => (
                      <MenuItem key={appointment.id} value={String(appointment.id)}>{appointment.label}</MenuItem>
                    )) : <MenuItem disabled>{noPackageAppointmentsMessage}</MenuItem>}
                  </Select>
                </FormControl>
                {packageAction.kind === "consume" ? (
                  <Alert severity="info">
                    {ft("messages.consumeHelp", "Package credits can be consumed only after the reserved appointment is completed or marked no-show. Completing the appointment also settles reserved package credits automatically.")}
                  </Alert>
                ) : null}
                {packageAction.kind === "reserve" ? (
                  <TextField label={t("spa.fields.quantity", { defaultValue: "Quantity" })} type="number" value={packageAction.quantity} onChange={(event) => setPackageAction((current) => ({ ...current, quantity: event.target.value }))} fullWidth />
                ) : null}
                {packageAction.kind === "release" ? (
                  <TextField label={ft("fields.reason", "Reason")} value={packageAction.reason} onChange={(event) => setPackageAction((current) => ({ ...current, reason: event.target.value }))} fullWidth />
                ) : null}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageAction(emptyPackageAction)}>{t("spa.common.cancel", { defaultValue: "Cancel" })}</Button>
          <Button variant="contained" onClick={submitPackageAction} disabled={working || (packageAction.kind === "reverse" ? !packageAction.reason : !packageAction.appointmentId) || (["consume", "release"].includes(packageAction.kind) && packageAppointmentOptions.length === 0)}>
            {t("spa.common.save", { defaultValue: "Save" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(storedValueAction.kind)} onClose={() => { if (!working) setStoredValueAction(emptyStoredValueAction); }} fullWidth maxWidth="sm">
        <DialogTitle>{ft("dialogs.storedValueAction", "Stored-value action")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {storedValueAction.kind === "status" ? (
              <>
                <FormControl fullWidth>
                  <InputLabel id="stored-value-status">{t("spa.fields.status", { defaultValue: "Status" })}</InputLabel>
                  <Select labelId="stored-value-status" label={t("spa.fields.status", { defaultValue: "Status" })} disabled={!canManageFinance || working} value={storedValueAction.status} onChange={(event) => setStoredValueAction((current) => ({ ...current, status: event.target.value }))}>
                    {["ACTIVE", "BLOCKED", "CANCELLED", "EXPIRED"].map((value) => <MenuItem key={value} value={value}>{enumLabel(value)}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label={ft("fields.reason", "Reason")} disabled={!canManageFinance || working} inputProps={{ maxLength: 500 }} value={storedValueAction.reason} onChange={(event) => setStoredValueAction((current) => ({ ...current, reason: event.target.value }))} fullWidth />
              </>
            ) : storedValueAction.kind === "reverse" ? (
              <TextField label={ft("fields.reason", "Reason")} disabled={!canManageFinance || working} inputProps={{ maxLength: 500 }} value={storedValueAction.reason} onChange={(event) => setStoredValueAction((current) => ({ ...current, reason: event.target.value }))} fullWidth />
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel id="stored-value-appointment">{ft("fields.appointment", "Appointment")}</InputLabel>
                  <Select labelId="stored-value-appointment" label={ft("fields.appointment", "Appointment")} disabled={!canManageFinance || working || !canViewAppointments} value={storedValueAction.appointmentId} onChange={(event) => setStoredValueAction((current) => ({ ...current, appointmentId: event.target.value }))}>
                    {storedValueAppointmentOptions.length ? storedValueAppointmentOptions.map((appointment) => (
                      <MenuItem key={appointment.id} value={String(appointment.id)}>{appointment.label}</MenuItem>
                    )) : <MenuItem disabled>{ft("messages.noClientAppointments", "No appointments found for this client")}</MenuItem>}
                  </Select>
                </FormControl>
                {["reserve", "consume"].includes(storedValueAction.kind) ? (
                  <TextField label={ft("fields.amount", "Amount")} type="number" disabled={!canManageFinance || working} inputProps={{ min: 0.01, step: 0.01 }} value={storedValueAction.amount} onChange={(event) => setStoredValueAction((current) => ({ ...current, amount: event.target.value }))} fullWidth />
                ) : null}
                {storedValueAction.kind === "release" ? (
                  <TextField label={ft("fields.reason", "Reason")} disabled={!canManageFinance || working} inputProps={{ maxLength: 500 }} value={storedValueAction.reason} onChange={(event) => setStoredValueAction((current) => ({ ...current, reason: event.target.value }))} fullWidth />
                ) : null}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={working} onClick={() => setStoredValueAction(emptyStoredValueAction)}>{t("spa.common.cancel", { defaultValue: "Cancel" })}</Button>
          <Button
            variant="contained"
            onClick={submitStoredValueAction}
            disabled={working || !canManageFinance
              || (storedValueAction.kind === "reverse" && !storedValueAction.reason.trim())
              || (["reserve", "consume"].includes(storedValueAction.kind) && (!canViewAppointments || !storedValueAction.appointmentId || !isPositiveMoney(storedValueAction.amount)))
              || (storedValueAction.kind === "release" && (!canViewAppointments || !storedValueAction.appointmentId))
              || (["BLOCKED", "CANCELLED"].includes(storedValueAction.status) && storedValueAction.kind === "status" && !storedValueAction.reason.trim())}
          >
            {t("spa.common.save", { defaultValue: "Save" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(generation.item)}
        onClose={() => {
          setGeneration(emptyAppointmentGeneration);
          setGenerationAvailability(null);
          setGenerationError("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{ft("dialogs.generateAppointment", "Generate package appointment")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={ft("fields.startAt", "Start at")}
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={generation.startAt}
              onChange={(event) => {
                setGenerationAvailability(null);
                setGenerationError("");
                setGeneration((current) => ({ ...current, startAt: event.target.value, selectedCombination: null }));
              }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{ft("fields.customerType", "Customer type")}</InputLabel>
              <Select label={ft("fields.customerType", "Customer type")} value={generation.customerType} onChange={(event) => setGeneration((current) => ({ ...current, customerType: event.target.value }))}>
                {["MEMBER", "HOTEL_GUEST", "EXTERNAL_GUEST"].map((value) => <MenuItem key={value} value={value}>{enumLabel(value)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{ft("fields.commercialStatus", "Commercial status")}</InputLabel>
              <Select label={ft("fields.commercialStatus", "Commercial status")} value={generation.commercialStatus} onChange={(event) => setGeneration((current) => ({ ...current, commercialStatus: event.target.value }))}>
                {["OPTION", "CONFIRMED", "GUARANTEED"].map((value) => <MenuItem key={value} value={value}>{t(`spa.statuses.${value}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{ft("fields.operators", "Operators")}</InputLabel>
              <Select
                multiple
                label={ft("fields.operators", "Operators")}
                value={generation.operatorIds}
                onChange={(event) => {
                  setGenerationAvailability(null);
                  setGenerationError("");
                  setGeneration((current) => ({
                    ...current,
                    operatorIds: normalizeSelectIds(event.target.value),
                    selectedCombination: null,
                  }));
                }}
                renderValue={(selected) => (
                  operatorOptions
                    .filter((operator) => normalizeSelectIds(selected).map(Number).includes(operator.id))
                    .map((operator) => operator.label)
                    .join(", ")
                )}
              >
                {operatorOptions.length ? operatorOptions.map((operator) => (
                  <MenuItem key={operator.id} value={String(operator.id)}>{operator.label}</MenuItem>
                )) : <MenuItem disabled>{ft("messages.noOperators", "No operators available")}</MenuItem>}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{ft("fields.cabins", "Cabins")}</InputLabel>
              <Select
                multiple
                label={ft("fields.cabins", "Cabins")}
                value={generation.cabinIds}
                onChange={(event) => {
                  setGenerationAvailability(null);
                  setGenerationError("");
                  setGeneration((current) => ({
                    ...current,
                    cabinIds: normalizeSelectIds(event.target.value),
                    selectedCombination: null,
                  }));
                }}
                renderValue={(selected) => (
                  cabinOptions
                    .filter((cabin) => normalizeSelectIds(selected).map(Number).includes(cabin.id))
                    .map((cabin) => cabin.label)
                    .join(", ")
                )}
              >
                {cabinOptions.length ? cabinOptions.map((cabin) => (
                  <MenuItem key={cabin.id} value={String(cabin.id)}>{cabin.label}</MenuItem>
                )) : <MenuItem disabled>{ft("messages.noCabins", "No cabins available")}</MenuItem>}
              </Select>
            </FormControl>
            <Button
              startIcon={generationChecking ? <CircularProgress size={18} /> : <EventAvailableIcon />}
              variant="outlined"
              onClick={checkGenerationAvailability}
              disabled={generationChecking || !generation.startAt || generation.operatorIds.length === 0 || generation.cabinIds.length === 0}
            >
              {ft("actions.checkAvailability", "Check availability")}
            </Button>
            {generationError ? (
              <Alert severity="warning" sx={{ whiteSpace: "pre-line" }}>
                {generationError}
              </Alert>
            ) : null}
            {generationAvailability ? (
              <Stack spacing={1}>
                <Alert severity={generationAvailability.available ? "success" : "warning"}>
                  {generationAvailability.available
                    ? ft("messages.availableCombinationCount", "{{count}} available combinations found", { count: generationAvailability.combinations?.length || 0 })
                    : availabilityMessage(generationAvailability, ft("messages.noCombinationForSelected", "No available combination for the selected time/resources"))}
                </Alert>
                {Array.isArray(generationAvailability.combinations) && generationAvailability.combinations.length ? (
                  <FormControl fullWidth>
                    <InputLabel>{ft("fields.availableCombination", "Available combination")}</InputLabel>
                    <Select
                      label={ft("fields.availableCombination", "Available combination")}
                      value={generation.selectedCombination ? JSON.stringify(generation.selectedCombination) : ""}
                      onChange={(event) => setGeneration((current) => ({
                        ...current,
                        selectedCombination: JSON.parse(event.target.value),
                      }))}
                    >
                      {generationAvailability.combinations.map((combination, index) => (
                        <MenuItem key={`${JSON.stringify(combination)}-${index}`} value={JSON.stringify(combination)}>
                          {combinationLabel(combination, operatorOptions, cabinOptions)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setGeneration(emptyAppointmentGeneration);
            setGenerationAvailability(null);
            setGenerationError("");
          }}>
            {t("spa.common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={submitAppointmentGeneration}
            disabled={working || !generation.startAt || generation.operatorIds.length === 0 || generation.cabinIds.length === 0}
          >
            {ft("actions.generate", "Generate")}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
}

export default SpaFinancePage;
